/**
 * ============================================================================
 * Check-in code — the QR the room actually scans
 * ============================================================================
 * The instructor opens a prebuilt session, launches the class, and generates
 * the code here. It belongs to that session and to that time window, so it is
 * also the time-and-date stamp on everyone who scans it.
 *
 * WHY THERE IS A BUTTON RATHER THAN A CODE ALREADY ON SCREEN. Only the hash of
 * the secret is stored, so the server genuinely cannot show you an existing
 * code — nobody can, including us. The plaintext exists once, in the response
 * to the request that mints it. That is the property that makes a stolen
 * database useless, and it is worth one click.
 *
 * WHY A LEAKED CODE IS NOT A HOLE. Two gates sit behind it. The session has to
 * be OPEN and inside its QR window, and attendance is not a record until the
 * instructor verifies the person on the roster. Someone who photographs the
 * screen and sends it on gets a dead end after the class ends, and never gets
 * an attendance record without the instructor. So this can be projected on a
 * wall without ceremony — which is the whole point of it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { apiFetch } from '../lib/api.js';
import ErrorBanner from './ErrorBanner.jsx';

/**
 * Error correction H — roughly 30% of the symbol can be unreadable and it still
 * scans. A code on a projector picks up glare, keystone distortion and the back
 * of somebody's head, none of which is true of a code on a desk.
 */
const QR_OPTIONS = { errorCorrectionLevel: 'H', margin: 2, width: 1000 };

/**
 * The address read out to the room. Taken from wherever this page is actually
 * being served rather than hardcoded — the site moved from attest-bc.netlify.app
 * to attest.withdueregard.com and the projected screen kept saying the old one.
 */
const siteHost = typeof window !== 'undefined' ? window.location.host : 'attest.withdueregard.com';

export function CheckInCode({ sessionId, sessionStatus }) {
  const [issued, setIssued] = useState(null);
  const [dataUrl, setDataUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [projecting, setProjecting] = useState(false);
  const closeRef = useRef(null);

  async function generate() {
    setError(null);
    setBusy(true);
    try {
      const result = await apiFetch(`/api/v1/sessions/${sessionId}/qr/rotate`, { method: 'POST' });
      setIssued(result);
      setDataUrl(await QRCode.toDataURL(result.qr.url, QR_OPTIONS));
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  // Escape leaves the projected view. A full-screen code with no visible way
  // out is a bad thing to be standing in front of a room with.
  const stopProjecting = useCallback(() => setProjecting(false), []);
  useEffect(() => {
    if (!projecting) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') stopProjecting();
    };
    window.addEventListener('keydown', onKey);
    closeRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [projecting, stopProjecting]);

  const live = sessionStatus === 'active';

  if (projecting && dataUrl && issued) {
    return (
      <div className="project" role="dialog" aria-modal="true" aria-label="Check-in code">
        <h2 className="project__title">Scan to check in</h2>
        <img className="project__qr" src={dataUrl} alt="QR code to check in to this session" />
        <p className="project__label">
          Can’t scan? Go to <strong>{siteHost}</strong> and enter
        </p>
        <p className="project__code">{issued.join.code}</p>
        <button type="button" className="btn btn--quiet project__close" onClick={stopProjecting} ref={closeRef}>
          Done (Esc)
        </button>
      </div>
    );
  }

  return (
    <section className="panel" aria-labelledby="code-heading">
      <h2 id="code-heading">Check-in code</h2>
      <ErrorBanner error={error} />

      {!live && (
        <p className="callout callout--warn">
          <strong>This session is not open yet.</strong>
          <span className="callout__detail">
            A code will generate, but nobody can check in until you launch the class — that is one of
            the two gates behind it. Open the session first.
          </span>
        </p>
      )}

      {!issued && (
        <>
          <p className="muted">
            The code belongs to this session and its time window, so it is also the time and date
            stamp on everyone who scans it. Only the hash is stored, so it can be shown once — if you
            need it again, generate a new one.
          </p>
          <button type="button" className="btn btn--primary" onClick={generate} disabled={busy}>
            {busy ? 'Generating…' : 'Generate the check-in code'}
          </button>
        </>
      )}

      {issued && dataUrl && (
        <>
          <div className="code-show">
            <img className="code-show__qr" src={dataUrl} alt="QR code to check in to this session" />
            <div className="code-show__side">
              <p className="muted">Read this out for anyone who can’t scan:</p>
              <p className="code-show__code">{issued.join.code}</p>
              <p className="muted">at {siteHost}</p>
              <div className="code-show__actions">
                <button type="button" className="btn btn--primary" onClick={() => setProjecting(true)}>
                  Show it full screen
                </button>
                <button type="button" className="btn btn--quiet" onClick={generate} disabled={busy}>
                  {busy ? 'Generating…' : 'Generate a new one'}
                </button>
              </div>
            </div>
          </div>
          <p className="callout callout--warn">
            <strong>Any code issued before this one has stopped working.</strong>
            <span className="callout__detail">
              Generating replaces it. If you have already printed or sent a code for this session,
              that copy is now dead — use this one.
            </span>
          </p>
        </>
      )}
    </section>
  );
}

export default CheckInCode;
